import { Box, IconButton, Theme, Tooltip, alpha, useTheme } from '@mui/material';
import { FunctionComponent, SVGProps, useEffect, useMemo, useState } from 'react';
import {
  BodyText,
  CaptionText,
  MinorHeading,
  Subheading
} from '../../shared/typography/PrimaryTypography';
import LastPageOutlinedIcon from '@mui/icons-material/LastPageOutlined';
import FirstPageOutlinedIcon from '@mui/icons-material/FirstPageOutlined';
import PrimaryDivider from '../../shared/decoration/PrimaryDivider';
import { MetricExtended, metric_label } from '../../../lib/metric_capture/metric';
import PrimarySkeleton from '../../shared/loading/PrimarySkeleton';
import { ReactComponent as MetricIcon } from '../../../assets/icons/Metrics.svg';
import { MetricGroupExtended } from '../../../lib/metric_capture/metric_group';
import { MetricRecordCaptureParameters } from '../../../views/metric_collection/MetricCollectionDataManagement';
import SearchBar from '../../shared/input/SearchBar';
import { ReactComponent as MissingIcon } from '../../../assets/icons/Doc Not Found.svg';
import PrimaryButton from '../../shared/button/PrimaryButton';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { ReactComponent as EnvironmentalIcon } from '../../../assets/icons/Environmental.svg';
import { ReactComponent as SocialIcon } from '../../../assets/icons/Social.svg';
import { ReactComponent as GovernanceIcon } from '../../../assets/icons/Government_Simplified.svg';
import { EsgCategory } from '@esg/esg-global-types';

interface MetricCollectionMetricsMenuProps {
  metrics: Array<MetricExtended>;
  capture_parameters: MetricRecordCaptureParameters;
  loading_metrics: boolean;
  main_content_ref: React.RefObject<HTMLDivElement>;
  handleParametersChange: (new_parameters: MetricRecordCaptureParameters) => void;
}

interface MetricCategoryFilter {
  title: string;
  esg_category: EsgCategory;
  color: string;
  icon: FunctionComponent<SVGProps<SVGSVGElement>>;
}

interface MetricFilters {
  search_text: string;
  category: MetricCategoryFilter | null;
}

/**
 * Metric selection menu of the metric collection capturing process.
 * @param {Array<MetricExtended>} metrics List of metrics to display from parent.
 * @param {MetricRecordCaptureParameters} capture_parameters Current capture parameters from parent.
 * @param {boolean} loading_metrics Loading state of parent metric entities to show feedback.
 * @param {React.RefObject<HTMLDivElement>} main_content_ref Reference to main metric collection data panel to control metrics menu height dynamically.
 * @param {(new_parameters: MetricRecordCaptureParameters): void} handleParametersChange Update capture parameters with metric data in parent.
 * @returns {JSX.Element}
 */
const MetricCollectionMetricsMenu = ({
  metrics,
  loading_metrics,
  capture_parameters,
  main_content_ref,
  handleParametersChange
}: MetricCollectionMetricsMenuProps) => {
  const [expanded, setExpanded] = useState<boolean>(true);
  const [metricFilters, setMetricFilters] = useState<MetricFilters>({
    search_text: '',
    category: null
  });
  const [mainContentHeight, setMainContentHeight] = useState<number | undefined>(undefined);
  const navigate: NavigateFunction = useNavigate();
  const theme: Theme = useTheme();
  const primary_blue: string = theme.palette.primary.main;

  // Filter buttons for ESG metric categories
  const metric_category_filters: Array<MetricCategoryFilter> = [
    {
      title: 'Environmental',
      esg_category: 'e',
      color: theme.palette.environmental.main,
      icon: EnvironmentalIcon
    },
    {
      title: 'Social',
      esg_category: 's',
      color: theme.palette.social.main,
      icon: SocialIcon
    },
    {
      title: 'Governance',
      esg_category: 'g',
      color: theme.palette.governance.main,
      icon: GovernanceIcon
    }
  ];

  // Navigate to data completeness view.
  const handleCaptureCompletenessClick = (): void => {
    navigate('/sustainability/metrics/data_completeness');
  };

  const handleExpandMenu = (expand: boolean): void => {
    setExpanded(expand);
  };

  const handleChangeSearchFilter = (new_search: string): void => {
    setMetricFilters({
      ...metricFilters,
      search_text: new_search
    });
  };

  const handleChangeCategoryFilter = (category_filter: MetricCategoryFilter): void => {
    setMetricFilters({
      ...metricFilters,
      category:
        metricFilters.category?.esg_category === category_filter.esg_category
          ? null
          : category_filter
    });
  };

  // Set a selected metric from the menu to the capture parameters
  const handleSelectMetric = (
    metric_group: MetricGroupExtended,
    metric: MetricExtended | null,
    is_quantitative: boolean
  ): void => {
    handleParametersChange({
      ...capture_parameters,
      metric: metric,
      metric_group: metric_group,
      is_quantitative: is_quantitative,
      average_over_year: is_quantitative && capture_parameters.average_over_year
    });
  };

  // Filter metrics from parent into lists of metric groups containing quantitative and qualitative metrics.
  const {
    quantitative_metric_groups,
    qualitative_metric_groups
  }: {
    quantitative_metric_groups: Map<string, MetricGroupExtended>;
    qualitative_metric_groups: Map<string, MetricGroupExtended>;
  } = useMemo(() => {
    // Initialise maps of metric group ids to metric group data with relative metrics.
    const quantitative_metric_groups = new Map<string, MetricGroupExtended>();
    const qualitative_metric_groups = new Map<string, MetricGroupExtended>();
    const lower_search_text: string = metricFilters.search_text.toLowerCase();
    metrics
      .filter((metric: MetricExtended) => {
        return Boolean(
          (lower_search_text
            ? metric.name.toLowerCase().includes(lower_search_text) ||
              metric.metric_group.name.toLowerCase().includes(lower_search_text)
            : true) &&
          (metricFilters.category
            ? metric.esg_category === metricFilters.category.esg_category
            : true)
        );
      })
      .forEach((metric: MetricExtended) => {
        const metric_group_id: string = metric.metric_group.id;
        if (
          'is_quantitative' in metric.standard &&
          metric.standard.is_quantitative &&
          !metric.is_calculated
        ) {
          quantitative_metric_groups.set(metric_group_id, {
            ...metric.metric_group,
            metrics: quantitative_metric_groups.has(metric_group_id)
              ? [...(quantitative_metric_groups.get(metric_group_id)?.metrics ?? []), metric]
              : [metric]
          });
        } else if (
          'is_quantitative' in metric.standard &&
          metric.standard.is_quantitative === false
        ) {
          qualitative_metric_groups.set(metric.metric_group.id, {
            ...metric.metric_group,
            metrics: qualitative_metric_groups.has(metric_group_id)
              ? [...(qualitative_metric_groups.get(metric_group_id)?.metrics ?? []), metric]
              : [metric]
          });
        }
      });

    return { quantitative_metric_groups, qualitative_metric_groups };
  }, [metrics, metricFilters]);

  // Update the height of the metrics menu to match the height of the main metric collection content dynamically.
  useEffect(() => {
    const updateHeight = (): void => {
      if (main_content_ref.current) {
        setMainContentHeight(main_content_ref.current.offsetHeight);
      }
    };
    updateHeight();
    const observer: ResizeObserver = new ResizeObserver(updateHeight);
    if (main_content_ref.current) {
      observer.observe(main_content_ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <Box
      sx={{
        ...{ ml: -3, mt: -3, mb: -3 },
        borderRight: `1px solid ${alpha(theme.palette.teal.dark, 0.4)}`,
        minWidth: '100px',
        maxWidth: '325px',
        width: expanded ? '100%' : '5%',
        transition: 'width 0.2s ease',
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        height: mainContentHeight
      }}
    >
      <Box
        sx={{
          boxSizing: 'border-box',
          mx: 3,
          borderBottom: `1px solid ${alpha(theme.palette.teal.dark, 0.4)}`,
          display: 'flex',
          flexDirection: 'column',
          height: '20vh'
        }}
      >
        <Box
          sx={{
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            height: '90px'
          }}
        >
          {expanded && <Subheading weight="Medium">METRICS</Subheading>}
          <IconButton onClick={() => handleExpandMenu(!expanded)} sx={{ ml: 'auto' }}>
            {expanded ? <FirstPageOutlinedIcon /> : <LastPageOutlinedIcon />}
          </IconButton>
        </Box>
        {expanded && (
          <Box sx={{ mb: 3 }}>
            <SearchBar
              variant="outlined"
              placeholder={`Search ${metric_label.many.toLowerCase()}...`}
              search_value={metricFilters.search_text}
              handleChangeSearch={handleChangeSearchFilter}
            />
          </Box>
        )}
      </Box>
      <Box sx={{ py: 1, flex: 1, overflow: expanded ? 'scroll' : 'auto', maxHeight: '120vh' }}>
        {expanded ? (
          <>
            <Box
              sx={{
                pr: 3,
                pl: 4,
                my: 3,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <PrimaryButton sx={{ mb: 1 }} onClick={handleCaptureCompletenessClick}>
                View Capture Completeness
              </PrimaryButton>
              <Box sx={{ display: 'flex', mt: 1, justifyContent: 'center', gap: 2, width: '100%' }}>
                {metric_category_filters.map((category_filter: MetricCategoryFilter) => {
                  const category_selected = Boolean(
                    metricFilters.category === null ||
                    metricFilters.category?.esg_category === category_filter.esg_category
                  );
                  const icon_color: string = category_selected
                    ? category_filter.color
                    : alpha(category_filter.color, 0.35);
                  const background_color: string = category_selected
                    ? alpha(category_filter.color, 0.3)
                    : alpha(category_filter.color, 0.1);
                  return (
                    <Tooltip title={category_filter.title} key={category_filter.esg_category}>
                      <Box
                        onClick={() => handleChangeCategoryFilter(category_filter)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 65,
                          height: 65,
                          borderRadius: 2,
                          backgroundColor: background_color,
                          cursor: 'pointer'
                        }}
                      >
                        {
                          <category_filter.icon
                            style={{
                              width: 40,
                              fill: icon_color,
                              stroke: icon_color,
                              strokeWidth: 1
                            }}
                          />
                        }
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
            {loading_metrics ? (
              <PrimarySkeleton sx={{ height: 40, m: 2 }} variant="rounded" />
            ) : (
              <Box sx={{ px: 3 }}>
                {quantitative_metric_groups.size > 0 || qualitative_metric_groups.size > 0 ? (
                  <>
                    {quantitative_metric_groups.size > 0 && (
                      <>
                        <Subheading weight="Medium" sx={{ my: 2 }}>
                          Numerical
                        </Subheading>
                        {Array.from(quantitative_metric_groups.values())
                          .sort((a: MetricGroupExtended, b: MetricGroupExtended) =>
                            a.name.localeCompare(b.name)
                          )
                          .map((metric_group: MetricGroupExtended) => (
                            <Box key={metric_group.id} sx={{ my: 2 }}>
                              <BodyText weight="Medium" sx={{ pl: 1 }}>
                                {metric_group.name}
                              </BodyText>
                              <PrimaryDivider opacity={0.3} sx={{ my: 0.5 }} />
                              {metric_group.metrics.map((metric: MetricExtended) => (
                                <Box
                                  key={metric.id}
                                  onClick={() => handleSelectMetric(metric_group, metric, true)}
                                  sx={{
                                    cursor: 'pointer',
                                    my: 0,
                                    py: 1,
                                    mx: -3,
                                    pl: 3,
                                    backgroundColor:
                                      capture_parameters.metric?.id === metric.id
                                        ? alpha(theme.palette.secondary.main, 0.1)
                                        : 'transparent'
                                  }}
                                >
                                  <CaptionText weight="Medium" sx={{ pl: 2 }}>
                                    {metric.name}
                                  </CaptionText>
                                </Box>
                              ))}
                            </Box>
                          ))}
                      </>
                    )}
                    {qualitative_metric_groups.size > 0 && (
                      <>
                        <PrimaryDivider
                          sx={{ mt: 3 }}
                          opacity={0.4}
                          color={theme.palette.teal.dark}
                        />
                        <Subheading weight="Medium" sx={{ my: 3 }}>
                          Disclosures
                        </Subheading>
                        {Array.from(qualitative_metric_groups.values())
                          .sort((a: MetricGroupExtended, b: MetricGroupExtended) =>
                            a.name.localeCompare(b.name)
                          )
                          .map((metric_group: MetricGroupExtended) => (
                            <Box
                              key={metric_group.id}
                              sx={{
                                my: 1,
                                py: 1,
                                px: 1,
                                mx: -3,
                                cursor: 'pointer',
                                backgroundColor:
                                  capture_parameters.metric_group?.id === metric_group.id &&
                                  capture_parameters.metric === null
                                    ? alpha(theme.palette.secondary.main, 0.1)
                                    : 'transparent'
                              }}
                              onClick={() => handleSelectMetric(metric_group, null, false)}
                            >
                              <BodyText weight="Medium" sx={{ pl: 3 }}>
                                {metric_group.name}
                              </BodyText>
                            </Box>
                          ))}
                      </>
                    )}
                  </>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      py: 4,
                      color: primary_blue
                    }}
                  >
                    <MissingIcon height={50} fill={primary_blue} stroke={primary_blue} />
                    <MinorHeading>No {metric_label.many} found.</MinorHeading>
                  </Box>
                )}
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, flexGrow: 1 }}>
            <MetricIcon height={50} fill={primary_blue} stroke={primary_blue} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MetricCollectionMetricsMenu;
